import type { CVData } from '~/types'
import { isPostingUrl } from '~/utils/postingUrl'
import { rememberGuestCv } from '~/composables/useTailorEntry'

/**
 * State and actions behind the chat CV builder: the AI asks one question at a
 * time, every answer is saved on the server (`cv_chat_sessions`), and once
 * there is enough the whole interview is turned into a CV.
 *
 * The interview is also kept in localStorage, so a reload or a slip of the
 * thumb on a phone does not throw twenty answers away.
 *
 * While the CV is being built, the page asks one thing itself (CvChatBuilder's
 * dialog): the job posting the CV is for. It is never sent to the AI that
 * writes the CV, so requirements of the posting cannot turn into experience.
 * The result offers to tailor the CV to it, with the price, and does not
 * tailor anything on its own (LandingResultOffers `initialPosting`).
 */
export interface CvChatMessage {
  role: 'assistant' | 'user'
  content: string
  /**
   * Kept out of the interview the server sees. `opening`: the first question
   * (an old CV or not?) and its answer. The posting step the chat used to ask
   * itself; only in interviews saved before the dialog. All dropped on restore.
   */
  kind?: 'opening' | 'posting-question' | 'posting'
}

/** A posting shorter than this is a job title, not an ad to compare with (TAILOR_DEMO_MIN_POSTING). */
export const CV_CHAT_MIN_POSTING = 80

export interface CvChatOptions {
  /** Label on the saved session and the guest CV, e.g. `ai-chat` (homepage) or `ai-chat-landing`. */
  source: string
  /** A/B variant the session belongs to, stored with it. */
  variant?: string
  /** Funnel events (`chat_started`, `chat_answer`, `chat_cv_built`…) for the A/B report. */
  onEvent?: (name: string, data?: Record<string, unknown>) => void
  /**
   * Where the interview is kept between visits. This browser's localStorage
   * by default; the signed-in app keeps it on the draft CV instead
   * (useCvChatDraft), so it shows up in the CV list and survives other devices.
   */
  storage?: CvChatStorage
}

export interface CvChatState {
  messages: CvChatMessage[]
  sessionId: string | null
  progress: number
  remaining: number
  done: boolean
  locale: string
  /** The posting from the build dialog: null before it was asked, '' when skipped. */
  posting?: string | null
}

export interface CvChatStorage {
  load: () => Partial<CvChatState> | null | Promise<Partial<CvChatState> | null>
  save: (state: CvChatState) => void
  clear: () => void
}

/**
 * How the interview opens. `ask`: the first message asks whether the visitor
 * already has a CV or a LinkedIn profile, answered by tapping yes or no (or by
 * typing, attaching or pasting right away in the answer box).
 * `import`: they have, and the next message asks them to attach it or paste
 * its text; the coach then asks whether to add anything before the CV is built. `interview`: they have
 * not, and the coach asks for everything from the name on.
 */
export type CvChatOpening = 'ask' | 'import' | 'interview'

const STORAGE_KEY = 'cvChatState:v1'
/** The coach "types" for a moment before replying to the first answer, like any other reply. */
const OPENING_REPLY_MS = 700
/** Mirrors MIN_ANSWERS in server/utils/cvChat. */
export const CV_CHAT_MIN_ANSWERS = 3
/** Mirrors PASTED_CV_CHARS in server/utils/cvChat: one answer this long is a pasted CV, enough to build from. */
export const CV_CHAT_PASTED_CV_CHARS = 600
/**
 * About how many answers a full interview takes (the coach's topic list in
 * /api/ai/cv-chat/message); shown until the coach gives its own estimate.
 */
export const CV_CHAT_TYPICAL_ANSWERS = 12
/** Mirrors MAX_ANSWER_CHARS in server/utils/cvChat; a whole pasted CV fits. */
export const CV_CHAT_MAX_ANSWER = 12000

const localStorageStore: CvChatStorage = {
  load() {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) as Partial<CvChatState> : null
  },
  save(state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  },
  clear() {
    localStorage.removeItem(STORAGE_KEY)
  },
}

type FetchError = { statusCode?: number; response?: { status?: number }; data?: { message?: string; statusMessage?: string; data?: { fairUse?: boolean } } }
const statusOf = (err: unknown) => (err as FetchError).statusCode ?? (err as FetchError).response?.status

export function useCvChat(options: CvChatOptions) {
  const { t, locale } = useI18n()
  const storage = options.storage ?? localStorageStore

  /** Before the first answer: asked about an old CV first, then the interview or the import. */
  const opening = ref<CvChatOpening>('ask')
  const greeting = (): CvChatMessage => ({ role: 'assistant', content: t('cvChat.import.question'), kind: 'opening' })

  const messages = ref<CvChatMessage[]>([greeting()])
  const input = ref('')
  const sessionId = ref<string | null>(null)
  const progress = ref(0)
  /** The coach's estimate of the answers still missing for a complete CV. */
  const remaining = ref(CV_CHAT_TYPICAL_ANSWERS)
  /** The AI has what it needs; the chat offers the build instead of the next question. */
  const done = ref(false)
  const suggestions = ref<string[]>([])
  /** The quick replies can be combined (duties, tools, skills): they are ticked, not sent one by one. */
  const multipleSuggestions = ref(false)
  const isThinking = ref(false)
  const isBuilding = ref(false)
  const error = ref<string | null>(null)
  /** Out of free questions today; the CV can still be built from what is there. */
  const limitReached = ref(false)
  /** The question limit hit was Premium's fair-use ceiling: there is no plan left to offer, only tomorrow. */
  const limitFairUse = ref(false)
  /** Out of free CV builds today: going on takes an account and Premium. */
  const buildLimitReached = ref(false)
  const { isAuthenticated } = useAuthUser()
  // Stamped on the saved interview and its CV, so /admin/journeys can tell where the visitor came from.
  const { visitorId, track } = useJourney()

  const generatedCV = ref<CVData | null>(null)
  const generationId = ref<string | null>(null)
  /** The posting the CV is for: null until answered, '' when the visitor has none. */
  const posting = ref<string | null>(null)

  /** The interview proper, as the server sees it: the posting step is the page's. */
  const transcript = computed(() => messages.value.filter(m => !m.kind).map(({ role, content }) => ({ role, content })))
  const answers = computed(() => transcript.value.filter(m => m.role === 'user').length)
  const hasStarted = computed(() => answers.value > 0)
  /** The visitor pasted their CV instead of answering: there is enough to build from after one message. */
  const hasPastedCv = computed(() => transcript.value.some(m => m.role === 'user' && m.content.length >= CV_CHAT_PASTED_CV_CHARS))
  /** Answers still needed before the CV can be built. */
  const answersLeft = computed(() => (hasPastedCv.value ? 0 : Math.max(0, CV_CHAT_MIN_ANSWERS - answers.value)))
  /**
   * The coach's "done" counts too: it may find a short pasted or attached CV
   * enough before `answersLeft` does, and its "the CV can be created now" must
   * not end on a disabled button (the server checks the session it saved as ready).
   */
  const canBuild = computed(() => (answersLeft.value === 0 || done.value) && !isThinking.value && !isBuilding.value)
  const canSend = computed(() => !!input.value.trim() && !isThinking.value && !isBuilding.value && !done.value && !limitReached.value)

  function persist() {
    try {
      storage.save({
        messages: messages.value,
        sessionId: sessionId.value,
        progress: progress.value,
        remaining: remaining.value,
        done: done.value,
        locale: locale.value,
        posting: posting.value,
      })
    } catch {
      /* private mode or full storage: the chat still works, it just won't survive a reload */
    }
  }

  function clearStored() {
    try {
      storage.clear()
    } catch {
      /* ignore */
    }
  }

  /** Picks up an interview left unfinished in this browser (or on the draft CV). */
  async function restore() {
    try {
      // A synchronous store (localStorage) restores before the builder's own
      // onMounted runs, so the greeting never starts typing over a restored chat.
      const loaded = storage.load()
      const state = loaded instanceof Promise ? await loaded : loaded
      if (!state) return
      const saved = Array.isArray(state.messages)
        ? state.messages
            .filter(m => (m?.role === 'user' || m?.role === 'assistant') && typeof m.content === 'string')
            // The posting used to be asked in the chat; the dialog asks it now.
            .filter(m => !m.kind)
            .map(m => ({ role: m.role, content: m.content }))
        : []
      // Answered already while a slow store was loading: what is on screen wins.
      if (!saved.some(m => m.role === 'user') || hasStarted.value) return
      messages.value = saved
      sessionId.value = typeof state.sessionId === 'string' ? state.sessionId : null
      progress.value = Number(state.progress) || 0
      remaining.value = Number.isFinite(Number(state.remaining)) ? Number(state.remaining) : CV_CHAT_TYPICAL_ANSWERS
      done.value = Boolean(state.done)
      posting.value = typeof state.posting === 'string' ? state.posting : null
    } catch {
      clearStored()
    }
  }

  // Before the first answer the opening question follows the language switcher.
  watch(locale, () => {
    if (hasStarted.value) return
    opening.value = 'ask'
    messages.value = [greeting()]
  })

  /**
   * The answer to the first question, tapped like a quick reply: it shows as
   * the visitor's message, and the coach replies. Without a CV the reply is
   * the interview's first question (the name and the job); with one, it asks
   * to attach the file or paste the text (`importCv`). The exchange so far
   * stays on screen but out of the interview the server sees, so a visitor who
   * changes their mind starts the interview from its first question.
   */
  async function chooseOpening(choice: Exclude<CvChatOpening, 'ask'>) {
    if (hasStarted.value || isThinking.value || isBuilding.value || opening.value === choice) return
    const answer = choice === 'import' ? 'cvChat.import.yes' : opening.value === 'import' ? 'cvChat.import.interview' : 'cvChat.import.no'
    opening.value = choice
    messages.value = [
      ...messages.value.map(message => ({ ...message, kind: 'opening' as const })),
      { role: 'user', content: t(answer), kind: 'opening' },
    ]
    track('chat_opening', { choice })

    isThinking.value = true
    await new Promise(resolve => setTimeout(resolve, OPENING_REPLY_MS))
    isThinking.value = false
    // Started over or switched language meanwhile
    if (opening.value !== choice || hasStarted.value) return
    messages.value.push({ role: 'assistant', content: t(choice === 'import' ? 'cvChat.import.ask' : 'cvChat.import.noCvGreeting') })
  }

  /**
   * The first question answered in the answer box instead of with a tap: the
   * question joins the interview the server sees, and the coach takes it from
   * there (asks for the CV on a yes, for the name on a no, or goes on from
   * whatever was written).
   */
  async function answerOpening(text: string) {
    if (!text.trim() || hasStarted.value || opening.value !== 'ask') return send(text)
    joinOpening()
    opening.value = 'interview'
    track('chat_opening', { choice: 'typed' })
    await send(text)
  }

  /** The unanswered first question becomes part of the interview, so the coach sees what was answered. */
  function joinOpening() {
    messages.value = messages.value.map(({ kind: _kind, ...message }) => message)
  }

  /**
   * An old CV or a LinkedIn profile, as text (read from the attached file or
   * pasted). Attached or pasted right away, without tapping yes first, it is
   * the answer to the first question. It goes into the interview as the
   * answer to "attach or paste your CV", so the coach and the build see it like
   * any pasted CV. Long enough to build from, the coach asks whether to add
   * anything first (a new job, a course), and the visitor adds it in the chat
   * or has the CV built (`asksAddAfterImport`); a fragment is sent as an
   * answer instead, and the coach asks for what is missing. Attached once the
   * interview is under way (the visitor changed their mind), it is the next
   * answer, and the coach goes on from it.
   */
  async function importCv(text: string, how: 'file' | 'paste') {
    const content = text.trim().slice(0, CV_CHAT_MAX_ANSWER)
    if (!content || isThinking.value || isBuilding.value) return
    if (hasStarted.value) {
      track('chat_import', { how, chars: content.length, midway: true })
      return send(content)
    }

    if (opening.value === 'ask') {
      joinOpening()
      opening.value = 'import'
    }
    track('chat_import', { how, chars: content.length })
    if (content.length < CV_CHAT_PASTED_CV_CHARS) return send(content)

    options.onEvent?.('chat_started', { import: how })
    messages.value.push({ role: 'user', content })
    if (how === 'paste') input.value = ''
    suggestions.value = []
    error.value = null
    persist()

    isThinking.value = true
    await new Promise(resolve => setTimeout(resolve, OPENING_REPLY_MS))
    isThinking.value = false
    // Started over meanwhile
    if (answers.value !== 1) return
    messages.value.push({ role: 'assistant', content: t('cvChat.import.anythingToAdd') })
    persist()
  }

  /**
   * The old CV is in and the coach asked whether to add anything: the chat
   * offers to build the CV as it is, next to the answer box for what to add.
   */
  const asksAddAfterImport = computed(() =>
    hasPastedCv.value && answers.value === 1 && !done.value && !isThinking.value
    && messages.value[messages.value.length - 1]?.role === 'assistant',
  )

  async function send(text?: string) {
    const content = (text ?? input.value).trim().slice(0, CV_CHAT_MAX_ANSWER)
    if (!content || isThinking.value || isBuilding.value || done.value || limitReached.value) return

    if (!hasStarted.value) options.onEvent?.('chat_started')

    messages.value.push({ role: 'user', content })
    input.value = ''
    suggestions.value = []
    error.value = null
    buildLimitReached.value = false
    isThinking.value = true
    persist()

    try {
      const response = await $fetch<{
        sessionId: string | null
        reply: string
        done: boolean
        progress: number
        remaining?: number
        suggestions: string[]
        multiple?: boolean
      }>('/api/ai/cv-chat/message', {
        method: 'POST',
        body: {
          messages: transcript.value,
          sessionId: sessionId.value,
          locale: locale.value,
          source: options.source,
          variant: options.variant,
          visitorId: visitorId(),
        },
      })

      sessionId.value = response.sessionId ?? sessionId.value
      if (response.reply) messages.value.push({ role: 'assistant', content: response.reply })
      progress.value = Math.max(progress.value, response.progress || 0)
      remaining.value = response.done ? 0 : (response.remaining ?? remaining.value)
      suggestions.value = response.suggestions ?? []
      multipleSuggestions.value = Boolean(response.multiple)
      done.value = response.done
      options.onEvent?.('chat_answer', { answers: answers.value, progress: progress.value })
      if (response.done) options.onEvent?.('chat_ready', { answers: answers.value })
    } catch (err) {
      console.error('CV chat error:', err)
      if (statusOf(err) === 429) {
        limitReached.value = true
        limitFairUse.value = Boolean((err as FetchError).data?.data?.fairUse)
        options.onEvent?.('chat_limit', { step: 'message', answers: answers.value, fair_use: limitFairUse.value, chat_session_id: sessionId.value })
        error.value = t(limitFairUse.value ? 'cvChat.errors.dailyLimitFairUse' : 'cvChat.errors.dailyLimit')
      } else {
        // The answer stays in the transcript; "try again" resends from there.
        error.value = t('cvChat.errors.reply')
      }
    } finally {
      isThinking.value = false
      persist()
    }
  }

  /**
   * The answer to the build dialog's question: the posting the CV is for, or
   * null when the visitor has none. False when the text is too short to be an
   * ad (a job title); the dialog then says so and waits.
   */
  function answerPosting(text: string | null): boolean {
    const content = (text ?? '').trim().slice(0, CV_CHAT_MAX_ANSWER)
    const skipped = !content
    if (!skipped && !isPostingUrl(content) && content.length < CV_CHAT_MIN_POSTING) return false
    posting.value = content
    options.onEvent?.('chat_posting', { given: !skipped, link: !skipped && isPostingUrl(content) })
    persist()
    return true
  }

  /** Asks again for the coach's reply to the last answer after a failed request. */
  async function retry() {
    const last = messages.value[messages.value.length - 1]
    if (last?.role !== 'user' || last.kind) return
    messages.value.pop()
    await send(last.content)
  }

  async function build() {
    if (!canBuild.value) return

    isBuilding.value = true
    error.value = null
    buildLimitReached.value = false

    try {
      const response = await $fetch<{ success: boolean; cvData: CVData; generationId?: string | null; sig?: string | null; sessionId?: string | null }>(
        '/api/ai/cv-chat/build',
        {
          method: 'POST',
          body: {
            messages: transcript.value,
            sessionId: sessionId.value,
            locale: locale.value,
            source: options.source,
            variant: options.variant,
            visitorId: visitorId(),
          },
        },
      )

      if (response.success && response.cvData) {
        generatedCV.value = response.cvData
        generationId.value = response.generationId ?? null
        sessionId.value = response.sessionId ?? sessionId.value
        // Without an account this browser is all that knows the CV: the
        // tailoring page offers it again (useTailorEntry).
        if (!isAuthenticated.value) rememberGuestCv(response.generationId, response.sig)
        options.onEvent?.('chat_cv_built', { answers: answers.value, chat_session_id: sessionId.value, generation_id: generationId.value })
      }
    } catch (err) {
      console.error('CV chat build error:', err)
      buildLimitReached.value = statusOf(err) === 429
      if (buildLimitReached.value) options.onEvent?.('chat_limit', { step: 'build', answers: answers.value, chat_session_id: sessionId.value })
      error.value = buildLimitReached.value
        ? t(isAuthenticated.value ? 'cvChat.errors.buildLimitMember' : 'cvChat.errors.buildLimit')
        : t('cvChat.errors.build')
    } finally {
      isBuilding.value = false
      persist()
    }
  }

  /**
   * Back from the result to the chat, answers kept, e.g. to add a job that was
   * left out. A CV built straight from an imported one ends on the visitor's
   * text, with nothing asked after it: the coach asks what to add.
   */
  function backToChat() {
    generatedCV.value = null
    generationId.value = null
    done.value = false
    if (messages.value[messages.value.length - 1]?.role === 'user') {
      messages.value.push({ role: 'assistant', content: t('cvChat.import.addMoreQuestion') })
      suggestions.value = []
    }
    persist()
  }

  function reset() {
    opening.value = 'ask'
    messages.value = [greeting()]
    input.value = ''
    sessionId.value = null
    progress.value = 0
    remaining.value = CV_CHAT_TYPICAL_ANSWERS
    done.value = false
    posting.value = null
    suggestions.value = []
    error.value = null
    buildLimitReached.value = false
    generatedCV.value = null
    generationId.value = null
    clearStored()
  }

  onMounted(restore)

  return {
    messages,
    input,
    sessionId,
    progress,
    remaining,
    done,
    suggestions,
    multipleSuggestions,
    isThinking,
    isBuilding,
    error,
    limitReached,
    limitFairUse,
    buildLimitReached,
    generatedCV,
    generationId,
    posting,
    answers,
    answersLeft,
    hasStarted,
    hasPastedCv,
    opening,
    chooseOpening,
    answerOpening,
    importCv,
    asksAddAfterImport,
    canBuild,
    canSend,
    send,
    retry,
    answerPosting,
    build,
    backToChat,
    reset,
  }
}
